import React from "react";
import AppShell from "@/components/ascend/AppShell";
import { useMusicPlayer, MiniPlayer } from "@/components/ascend/MusicPlayer";
import { Music4, Play, Pause } from "lucide-react";

function fmtDur(secs) {
  const s = Math.max(0, Number(secs) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function MusicPage() {
  const player = useMusicPlayer({ initialVolume: 30 });
  const BE = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <AppShell>
      <div className="pt-2 mb-6 fade-up">
        <span className="eyebrow">background quiet</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Focus Music</h1>
        <p className="text-muted mt-1 max-w-xl">Six curated pieces — public-domain classical. Play alongside your study.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8" data-testid="music-tracklist">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {player.tracks.map((t, i) => {
              const active = player.current === i;
              const isPlaying = active && player.playing;
              const artUrl = t.artwork_url ? BE + t.artwork_url : null;
              return (
                <button
                  key={t.id}
                  onClick={() => (isPlaying ? player.pauseFade(500) : player.playTrackByIndex(i))}
                  className="group text-left card-soft-2 p-3 flex flex-col gap-3 transition-transform hover:-translate-y-0.5"
                  style={{
                    borderColor: active ? "var(--primary)" : "var(--border)",
                    borderWidth: active ? 2 : 1,
                  }}
                  data-testid={`music-track-${t.id}`}
                >
                  <div
                    className="relative aspect-square w-full rounded-xl overflow-hidden bg-cover bg-center"
                    style={{
                      backgroundImage: artUrl ? `url(${artUrl})` : "none",
                      background: !artUrl ? "color-mix(in oklab, var(--primary-soft) 55%, transparent)" : undefined,
                    }}
                  >
                    <div
                      className="absolute inset-0 flex items-center justify-center transition-opacity"
                      style={{
                        background: "linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0) 55%)",
                        opacity: active ? 1 : 0,
                      }}
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: "rgba(0,0,0,0.15)" }}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                        style={{ background: "var(--primary)", color: "#fff" }}
                      >
                        {isPlaying ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} />}
                      </div>
                    </div>
                    {isPlaying && (
                      <div
                        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ background: "var(--primary)", color: "#fff" }}
                        aria-label="Now playing"
                      >
                        <Music4 size={14} strokeWidth={1.75} />
                      </div>
                    )}
                    {!t.is_procedural ? (
                      <span
                        className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.85)", color: "var(--primary)" }}
                      >PD</span>
                    ) : (
                      <span
                        className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.85)", color: "var(--accent)" }}
                      >demo</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg leading-tight truncate" style={{ color: "var(--text)" }}>{t.title}</div>
                    <div className="text-xs text-muted truncate">
                      {t.composer} · {fmtDur(t.duration_seconds)}
                    </div>
                    {t.performer && (
                      <div className="text-[11px] text-muted truncate italic mt-0.5" title={t.performer}>{t.performer}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
        <aside className="lg:col-span-4 lg:sticky lg:top-6 self-start">
          <MiniPlayer player={player} />
          <p className="text-xs text-muted mt-3 italic leading-relaxed">
            Recordings sourced from <a href="https://commons.wikimedia.org/wiki/Category:Audio_files_of_classical_music_for_piano" target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>Wikimedia Commons</a> — all public domain or CC0. Attribution per track available via the API.
          </p>
        </aside>
      </div>
    </AppShell>
  );
}
